<script>
import helpers from '@/lib/helpers'
import ModelChooser from '../layout/model-chooser.vue'
import CustomerRow from '../customer/customer-row.vue'
import VendorRow from '../vendor/vendor-row.vue'

/**
 * One shared photo gallery: its name, note, link, whether it is private and
 * when it expires - and its photos, which take uploads and pastes like any
 * other gallery. Opened on an existing gallery (id) or to make a new one,
 * optionally with photos to start it with (the Share menu's selection).
 * Lives inside a modal (the Photo Galleries list, or its own for the Share
 * menu): 'done' carries the gallery as it stands when closed, or null when
 * it was deleted.
 */
export default {
  name: 'gallery-edit',

  props: {
    id: {type: Number, default: null},
    photos: {type: Array, default: () => []},
  },

  data() {
    return {
      gallery: null,
      form: {name: '', private: false, expires: this.defaultExpiry(), fkcustomer: null, fkvendor: null},
      // who it is restricted to, for showing (the form carries the ids)
      restrictTo: null,
      saving: false,
    }
  },

  computed: {
    isNew() {
      return !this.gallery
    },
    changed() {
      if (this.isNew)
        return true
      return this.form.name !== (this.gallery.name || '')
          || this.form.private !== !!this.gallery.private || this.form.expires !== this.dateOf(this.gallery.expires)
          || this.form.fkcustomer !== (this.gallery.customer ? this.gallery.customer.id : null)
          || this.form.fkvendor !== (this.gallery.vendor ? this.gallery.vendor.id : null)
    },
    status() {
      if (!this.gallery)
        return null
      if (this.gallery.private)
        return {text: 'Private - the link does not answer', variant: 'secondary'}
      if (this.gallery.expired)
        return {text: 'Expired ' + this.$options.filters.timediff(this.gallery.expires), variant: 'warning'}
      return {text: this.gallery.expires ? 'Live until ' + new Date(this.gallery.expires).toLocaleDateString() : 'Live, never expires', variant: 'success'}
    },
  },

  mounted() {
    if (this.id)
      this.load()
  },

  methods: {
    defaultExpiry() {
      const d = new Date()
      d.setDate(d.getDate() + 30)
      return d.toISOString().substr(0, 10)
    },
    dateOf(iso) {
      return iso ? iso.substr(0, 10) : ''
    },
    async load() {
      this.take(await this.asyncAction('/api/gallery/' + this.id, 'GET'))
    },
    /**
     * The gallery's photos changed ({added} or {removed}): keep this record's
     * list - and the count in the card title - in step from the event, rather
     * than fetching the whole gallery again, which with a few hundred photos
     * is another full round trip and render on top of the paste itself.
     */
    photosChanged(ev) {
      if (!this.gallery || !ev)
        return
      if (ev.added) {
        const have = new Set(this.gallery.photos.map(p => p.id))
        this.gallery.photos = this.gallery.photos.concat(ev.added.filter(p => !have.has(p.id)))
      }
      if (ev.removed) {
        const gone = new Set(ev.removed.map(p => p._links && p._links.delete_url && p._links.delete_url.href))
        this.gallery.photos = this.gallery.photos.filter(p => !(p._links && p._links.delete_url && gone.has(p._links.delete_url.href)))
      }
    },
    take(g) {
      this.gallery = g
      this.form = {
        name: g.name || '', private: !!g.private, expires: this.dateOf(g.expires),
        fkcustomer: g.customer ? g.customer.id : null, fkvendor: g.vendor ? g.vendor.id : null,
      }
      this.restrictTo = g.customer ? {type: 'Customer', ...g.customer} : (g.vendor ? {type: 'Vendor', ...g.vendor} : null)
    },
    async save() {
      this.saving = true
      try {
        const data = {...this.form, name: this.form.name.trim()}
        if (this.isNew) {
          data.photo_add = this.photos.map(p => ({id: p.id}))
          this.take(await this.asyncAction('/api/gallery', 'POST', {data}))
          helpers.toast('Gallery made' + (this.photos.length ? ' with ' + this.photos.length + ' photo(s)' : ''))
        } else {
          this.take(await this.asyncAction('/api/gallery/' + this.gallery.id, 'PUT', {data}))
          helpers.toast('Gallery saved')
        }
      } finally {
        this.saving = false
      }
    },
    async remove() {
      if (!await helpers.confirm('Delete ' + (this.gallery.name ? 'the gallery "' + this.gallery.name + '"' : 'this gallery') + '? The photos themselves are kept.'))
        return
      await this.asyncAction('/api/gallery/' + this.gallery.id, 'DELETE')
      helpers.toast('Gallery deleted')
      this.$emit('done', null)
    },
    /** Restrict the link to a customer's or a vendor's signed-in portal contacts. */
    async restrict(type) {
      const picked = type === 'Customer'
          ? await this.$dialog.showAndWait(ModelChooser, {formatter: CustomerRow, formatterKey: 'customer', url: '/api/customer'})
          : await this.$dialog.showAndWait(ModelChooser, {formatter: VendorRow, formatterKey: 'vendor', url: '/api/vendor'})
      if (!picked || !picked.id)
        return
      this.restrictTo = {type, id: picked.id, gli: picked.gli, company: picked.company}
      this.form.fkcustomer = type === 'Customer' ? picked.id : null
      this.form.fkvendor = type === 'Vendor' ? picked.id : null
    },
    unrestrict() {
      this.restrictTo = null
      this.form.fkcustomer = null
      this.form.fkvendor = null
    },
    copyLink() {
      helpers.copy_to_clipboard(this.gallery.share_url)
    },
    close() {
      this.$emit('done', this.gallery)
    },
  },
}
</script>

<template>
  <div class="gallery-edit px-2 pt-2">
    <!-- the GLI (click to copy) and the link, in the modal's top strip above the scrolling body -->
    <modal-portal to="top" v-if="gallery">
      <div class="d-flex align-items-center">
      <div class="h4 mb-0 mr-3 text-nowrap">
        <text-copy>{{ gallery.gli }}</text-copy>
      </div>
      <b-input-group size="sm" class="flex-grow-1">
        <b-input-group-prepend is-text>Link</b-input-group-prepend>
        <b-form-input :value="gallery.share_url" readonly class="text-monospace" @focus="$event.target.select()"/>
        <b-input-group-append>
          <b-button variant="outline-secondary" class="text-nowrap" @click="copyLink">
            <font-awesome-icon :icon="['far', 'copy']" class="mr-1"/>Copy
          </b-button>
          <b-button variant="outline-secondary" class="text-nowrap" :href="gallery.share_url" target="_blank">
            <font-awesome-icon :icon="['fas', 'arrow-up-right-from-square']" class="mr-1"/>Open
          </b-button>
        </b-input-group-append>
      </b-input-group>
      </div>
    </modal-portal>

    <!-- settings on the left, notes on the right; the photos below, full width.
         No gutters: the row's negative margins would overflow the modal body sideways -->
    <b-row no-gutters>
      <b-col md="6" class="pr-md-3 mb-2">
        <form-card title="Details">
        <b-tabs small content-class="pt-3">
          <b-tab title="Details" active>
            <b-form-group label="Name (optional)" label-size="sm" class="mb-2">
              <b-form-input v-model="form.name" size="sm" maxlength="120" autofocus placeholder="Shown under the page's Gallery heading" @keydown.enter="save"/>
            </b-form-group>
            <b-form-group label="Expires" label-size="sm" class="mb-2" description="Blank: the link never expires.">
              <b-form-input v-model="form.expires" type="date" size="sm" :disabled="form.private"/>
            </b-form-group>
            <div v-if="gallery" class="small text-muted mb-2">
              Made {{ gallery.created | timediff }}<template v-if="gallery.createdby"> by {{ gallery.createdby.userid }}</template>
            </div>
            <div v-if="gallery" class="small">
              <b-badge :variant="status.variant">{{ status.text }}</b-badge>
            </div>
          </b-tab>

          <b-tab title="Security">
            <b-form-checkbox v-model="form.private" size="sm" class="mb-3" :disabled="gallery && !gallery.mine">
              Private - only I see it in OMS, and the link does not answer
            </b-form-checkbox>
            <b-form-group label="Restricted to" label-size="sm" class="mb-2" description="Only their signed-in portal contacts can open the link.">
              <div v-if="restrictTo" class="d-flex align-items-center small">
                <span class="text-truncate"><strong>{{ restrictTo.type }}</strong> {{ restrictTo.gli }} {{ restrictTo.company }}</span>
                <b-button size="sm" variant="link" class="p-0 ml-2" @click="unrestrict" title="Anyone with the link">
                  <font-awesome-icon :icon="['fas', 'xmark']"/>
                </b-button>
              </div>
              <b-button-group v-else size="sm">
                <b-button variant="outline-secondary" @click="restrict('Customer')">Customer…</b-button>
                <b-button variant="outline-secondary" @click="restrict('Vendor')">Vendor…</b-button>
              </b-button-group>
            </b-form-group>
          </b-tab>
        </b-tabs>
        </form-card>
      </b-col>

      <!-- the standard notes, their own box with their own buttons (the API says who may read them) -->
      <b-col md="6" v-if="gallery" class="mb-2">
        <form-card title="Notes">
          <notes :url="gallery._links.note_url.href" height="auto" max-height="360px" write="galleries.notes.write"/>
        </form-card>
      </b-col>
    </b-row>

    <form-card v-if="gallery" :title="'Photos (' + gallery.photos.length + ')'">
      <photo-gallery
          :photos="gallery.photos"
          :upload-url="gallery._links.photo_url.href"
          :label="'Gallery ' + (gallery.name || gallery.gli)"
          :width="140"
          with-filename
          @change="photosChanged"/>
    </form-card>
    <div v-else-if="photos.length" class="small text-muted">
      Starts with {{ photos.length }} photo{{ photos.length === 1 ? '' : 's' }}; more can be added once it is made.
      <div class="d-flex flex-wrap mt-2 gallery-start">
        <b-img v-for="p in photos" :key="p.id" :src="p.thumbs ? p.thumbs.small : p.url" class="mr-1 mb-1 rounded border" width="56" height="56" style="object-fit: cover"/>
      </div>
    </div>

    <!-- the buttons in the surrounding modal's footer -->
    <modal-portal to="footer">
      <div class="d-flex align-items-center">
        <b-button v-if="!isNew" size="sm" variant="outline-danger" @click="remove">Delete</b-button>
        <b-button size="sm" variant="primary" class="px-4 ml-auto" :disabled="!changed || saving" @click="save">{{ isNew ? 'Create' : 'Save' }}</b-button>
      </div>
    </modal-portal>
  </div>
</template>

<style scoped>
.gallery-edit {
  overflow-x: hidden;
}
</style>


